import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import {extractErrorMessage, goBack} from 'src/app/common/common';
import { Channel, ChannelPowertypes, RidViewModel } from 'src/app/model/rids.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {HttpParams} from "@angular/common/http";

@Component({
  selector: "app-create-rid",
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: "./create-rid.component.html",
  styleUrl: "./create-rid.component.scss",
})
export class CreateRidComponent implements OnInit {
  unsubscribe = new Subject<void>();
  formsSubscribe: any;
  error?: string;

  // FORM
  ridForm = new FormGroup({
    id: new FormControl<string>(''),
    external_id: new FormControl<string>(''),
    channel: new FormControl<string>(''),
    aggregationType: new FormControl<string | null>(null),
    dataSource: new FormControl<string>(''),
    sampleRateMin: new FormControl<string>(''),
    resourceId: new FormControl<string>(''),
    market_context: new FormControl<string>(''),
    sampleRateMax: new FormControl<string>(''),
    maximumValue: new FormControl<Number>(0),
    minimumValue: new FormControl<Number>(0),
    mrid: new FormControl<string>(''),
  });

  public editing: boolean = false;
  public platformId: string;
  public router: Router;
  public channels: Channel[] = [];
  public channels_map: { [key: string]: ChannelPowertypes[] } = {};
  public aggregation_types: string[] = ['portfolio', 'registration', 'product'];

  public spid = {
    label: '',
    channel: ''
  };

  public rid: RidViewModel = {
    external_id: '',
    data_source: null,
    market_context: "",
    resource_id: '',
    sampleRateMin: "",
    sampleRateMax: "",
    power_type: {
      itemLabel: "",
      siScaleCode: "",
      itemUnit: "",
      hertz: "",
      voltage: "",
    },
    id: '',
    mrid: '',
  };

  public powerTypes: any[] = [];
  public channel_powertypes: ChannelPowertypes[] = [];
  public filteredPowertypes: ChannelPowertypes[] = [];
  public cpSelected: ChannelPowertypes | undefined;

  constructor(
    private injector: Injector,
    private createRidsService: ApiService
  ) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.platformId = event.snapshot.params.id;
      }
    });
  }

  private disableFormParams() {
    if (this.editing) {
      this.ridForm.controls.id.disable();

      if (this.rid.data_source === 'x-energy') {
        this.ridForm.controls.channel.disable();
      }

      if (this.rid.data_source) {
        this.ridForm.controls.dataSource.disable();
      }

      if (this.rid.data_source === 'basic') {
        this.ridForm.controls.aggregationType.disable();
      }
    } else {
      this.ridForm.controls.id.enable();
      this.ridForm.controls.channel.enable();
      this.ridForm.controls.dataSource.enable();
      this.ridForm.controls.aggregationType.enable();
    }
  }

  setFormsDefaultValue() {
    if (this.editing) {
      if (this.rid.data_source === 'x-energy') {
        // Split the ID for x-energy data source
        const parts = this.rid.id.split(':');
        this.spid.label = parts[0];
        this.spid.channel = parts[1];
        this.ridForm.controls.id.setValue(this.spid.label);
        this.ridForm.controls.channel.setValue(this.spid.channel);
      } else {
        this.spid.label = this.rid.id;
        this.spid.channel = this.rid.channel || '';
        this.ridForm.controls.id.setValue(this.rid.id);
        this.ridForm.controls.channel.setValue(this.rid.channel);
      }

      if (this.rid.data_source === 'static') {
        this.rid.maximum_value = parseFloat(this.rid.maximum_value?.toString() || '0');
        this.rid.minimum_value = parseFloat(this.rid.minimum_value?.toString() || '0');
      }
      if (this.rid.power_type_id) {
        this.cpSelected = this.filteredPowertypes.find(cp => cp.uuid === this.rid.power_type_id);
        this.updateFilteredPowertypes();
      }
    } else {
      this.ridForm.controls.id.setValue('');
      this.ridForm.controls.channel.setValue('');
      this.cpSelected = undefined; // Clear selection for new RID
    }

    this.ridForm.controls.external_id.setValue(this.rid.external_id || '');
    this.ridForm.controls.aggregationType.setValue(this.rid.aggregation_type || null);
    this.ridForm.controls.market_context.setValue(this.rid.market_context || '');
    this.ridForm.controls.maximumValue.setValue(this.rid.maximum_value || 0);
    this.ridForm.controls.dataSource.setValue(this.rid.data_source || '');
    this.ridForm.controls.minimumValue.setValue(this.rid.minimum_value || 0);
    this.ridForm.controls.resourceId.setValue(this.rid.resource_id || '');
    this.ridForm.controls.sampleRateMax.setValue(this.rid.sampleRateMax || '');
    this.ridForm.controls.sampleRateMin.setValue(this.rid.sampleRateMin || '');
    this.ridForm.controls.mrid.setValue(this.rid.mrid || '');

    this.updateFilteredPowertypes();
    this.disableFormParams();
  }

  getAll() {
    const powerTypesQueryParams: CustomHttpParams = {
      'depth': 1
    };
    const channelQueryParams: CustomHttpParams = {
      'depth': 3
    };
    forkJoin([
      this.createRidsService.getPowerType(powerTypesQueryParams).pipe(takeUntil(this.unsubscribe)),
      this.createRidsService.getChannel(channelQueryParams).pipe(takeUntil(this.unsubscribe)),
    ]).subscribe(([powerTypes, channels]) => {
      if (powerTypes && powerTypes.length) {
        this.powerTypes = powerTypes;
      }
      if (channels && channels.length) {
        this.channels = channels;
        this.channels_map = {};
        const channel_powertypes_set = new Set();

        channels.forEach((item: any) => {
          this.channels_map[item.id] = [];
          if (item.channel_powertypes) {
            item.channel_powertypes.forEach((cp: any) => {
              this.channels_map[item.id].push(cp);
              channel_powertypes_set.add(cp);
            });
          }
        });

        this.channel_powertypes = [...channel_powertypes_set] as ChannelPowertypes[];
      }

      // If editing, get the specific RID
      if (this.editing && this.platformId) {
        const queryParams: CustomHttpParams = {
          'depth': 2,
          'id': this.platformId,
        };
        this.createRidsService.getRids(queryParams)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe((rids) => {
            if (rids && rids.length) {
              this.rid = rids[0];
            } else {
              this.editing = false;
              this.rid.mrid = '';
            }
            this.setFormsDefaultValue();
          });
      } else {
        // For new RID creation
        this.editing = false;
        this.rid.mrid = '';
        this.setFormsDefaultValue();
      }
    });

    this.setupFormSubscriptions();
  }

  setupFormSubscriptions() {
    this.ridForm.controls.id.valueChanges.subscribe(
      (val: any) => this.spid.label = val
    );

    this.ridForm.controls.external_id.valueChanges.subscribe(
      (val: any) => this.rid.external_id = val
    );

    this.ridForm.controls.channel.valueChanges.subscribe(
      (val: any) => {
        this.spid.channel = val;
        this.updateFilteredPowertypes();
      }
    );

    this.ridForm.controls.aggregationType.valueChanges.subscribe(
      (val: any) => this.rid.aggregation_type = val
    );

    this.ridForm.controls.market_context.valueChanges.subscribe(
      (val: any) => this.rid.market_context = val
    );

    this.ridForm.controls.maximumValue.valueChanges.subscribe(
      (val: any) => this.rid.maximum_value = val
    );

    this.ridForm.controls.dataSource.valueChanges.subscribe(
      (val: any) => this.rid.data_source = val
    );

    this.ridForm.controls.minimumValue.valueChanges.subscribe(
      (val: any) => this.rid.minimum_value = val
    );

    this.ridForm.controls.resourceId.valueChanges.subscribe(
      (val: any) => this.rid.resource_id = val
    );

    this.ridForm.controls.sampleRateMax.valueChanges.subscribe(
      (val: any) => this.rid.sampleRateMax = val
    );

    this.ridForm.controls.sampleRateMin.valueChanges.subscribe(
      (val: any) => this.rid.sampleRateMin = val
    );

    this.ridForm.controls.mrid.valueChanges.subscribe(
      (val: any) => this.rid.mrid = val
    );
  }

  updateFilteredPowertypes() {
    if (this.spid.channel && this.spid.channel !== '' && this.channels_map[this.spid.channel]) {
      this.filteredPowertypes = this.channels_map[this.spid.channel];
    } else {
      this.filteredPowertypes = this.channel_powertypes;
    }
  }

  ngOnInit(): void {
    this.getAll();
  }

  isFormValid(): boolean {
    const form = this.ridForm;
    const rid = this.rid;

    if (!this.spid.label || !rid.market_context || !rid.resource_id ||
      !rid.sampleRateMin || !rid.sampleRateMax || !rid.data_source) {
      return false;
    }

    if (rid.data_source === 'x-energy' && !this.spid.channel) {
      return false;
    }

    if (rid.data_source === 'basic' && !rid.aggregation_type) {
      return false;
    }

    if (rid.data_source === 'static' &&
      (rid.minimum_value === null || rid.minimum_value === undefined ||
        rid.maximum_value === null || rid.maximum_value === undefined)) {
      return false;
    }

    if (!rid.power_type_id) {
      return false;
    }

    return true;
  }

  createRidId(): string {
    return this.spid.label + (this.rid.data_source === 'x-energy' ? ':' + this.spid.channel : '');
  }

  save() {
    if (!this.isFormValid()) {
      this.error = 'Please fill out all fields correctly and try again.';
      return;
    }

    if (this.rid.data_source === 'static') {
      if (parseFloat(this.rid.minimum_value?.toString() || '0') > parseFloat(this.rid.maximum_value?.toString() || '0')) {
        this.error = 'Static maximum value must be greater than static minimum value.';
        return;
      }
    }
    this.error = undefined;
    this.rid.id = this.createRidId();

    if (this.editing) {
      this.createRidsService.editRids(this.rid).subscribe({
        next: (_) => goBack('/rids', this.router),
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err, "Failed to update RID.");
        }
      });
    } else {
      this.createRidsService.createRids(this.rid).subscribe({
        next: (_) => goBack('/rids', this.router),
        error: (err) => {
          console.error(err);
          this.error = this.error = extractErrorMessage(err, "Failed to create RID.");
        }
      });
    }
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  useDataSources(event: any) {
    this.rid.data_source = event.value;
    this.spid.channel = '';
    this.updateFilteredPowertypes();
  }

  changeChannels(event: any) {
    this.spid.channel = event.value;
    this.ridForm.controls.channel.setValue(event.value);
    this.updateFilteredPowertypes();
  }

  changeAggType(event: any) {
    this.ridForm.controls.aggregationType.setValue(event.value);
  }

  usePowerType(event: any) {
    this.rid.power_type = this.channel_powertypes.find((cp) => cp.uuid === event.value)?.powertype;
    this.rid.power_type_id = event.value;
  }
}
